import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useApp } from '../hooks/useApp';
import { useAuth } from '../contexts/AuthContext';
import ThemeToggle from '../components/dashboard/ThemeToggle';
// import { AdSidebar } from '../components/ads'; // Disabled for now
import { 
  HomeIcon,
  ChartBarIcon,
  TrophyIcon,
  UserIcon,
  CogIcon,
  ArrowRightOnRectangleIcon,
  ArrowLeftOnRectangleIcon,
  Bars3Icon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const DashboardLayout = ({ children }) => {
  const navigate = useNavigate();
  const { _isDarkMode } = useTheme();
  const { username } = useApp();
  const { logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: ChartBarIcon, current: true, available: true },
    { name: 'Profile', href: '/profile', icon: UserIcon, current: false, available: false },
    { name: 'Rewards', href: '/rewards', icon: TrophyIcon, current: false, available: false },
    { name: 'Settings', href: '/settings', icon: CogIcon, current: false, available: false },
  ];

  const handleEnterGame = () => {
    navigate('/game-lobby');
    setSidebarOpen(false); // Close sidebar on mobile after navigation
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  // Sidebar content component for reuse
  const SidebarContent = ({ className = "" }) => (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Logo */}
      <div className="flex items-center flex-shrink-0 px-4 pt-5 mb-2">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-lg">💣</span>
          </div>
          <div>
            <p className="text-xl font-bold text-gray-900 dark:text-white">
              Bomberman
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Hunt</p>
          </div>
        </div>
      </div>

      {/* Enter Game Button */}
      <div className="px-4 mt-6">
        <button
          onClick={handleEnterGame}
          className="w-full flex items-center justify-center px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl border border-blue-400/20"
        >
          <ArrowRightOnRectangleIcon className="h-5 w-5 mr-2" />
          Enter Game
        </button>
      </div>

      {/* Navigation */}
      <nav className="mt-8 flex-1 px-2 space-y-1">
        {navigation.map((item) => {
          const Icon = item.icon;
          const isAvailable = item.available;
          
          return (
            <button
              key={item.name}
              onClick={isAvailable ? () => {
                navigate(item.href);
                closeSidebar();
              } : undefined}
              disabled={!isAvailable}
              className={`group flex items-center justify-between px-2 py-2 text-sm font-medium rounded-md w-full text-left transition-colors duration-200 ${
                item.current
                  ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                  : isAvailable
                    ? 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white cursor-pointer'
                    : 'text-gray-500 dark:text-gray-400 cursor-not-allowed opacity-80 bg-gray-50/50 dark:bg-gray-800/30'
              }`}
            >
              <div className="flex items-center">
                <Icon
                  className={`mr-3 flex-shrink-0 h-5 w-5 ${
                    item.current
                      ? 'text-blue-500 dark:text-blue-400'
                      : isAvailable
                        ? 'text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300'
                        : 'text-gray-500 dark:text-gray-400'
                  }`}
                />
                {item.name}
              </div>
              {!isAvailable && (
                <span className="text-[10px] font-bold  text-amber-700 dark:text-amber-300 px-2.5 py-1 rounded-full shadow-sm">
                  Coming Soon
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Ad Section in Sidebar - Disabled for now */}
      {/* <div className="px-2 mt-4">
        <AdSidebar provider="auto" />
      </div> */}

      {/* User info and controls at bottom */}
      <div className="flex-shrink-0 p-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
        {/* Username display */}
        <div className="flex items-center space-x-2">
          <UserIcon className="h-4 w-4 text-gray-400" />
          <span className="text-md font-bold text-gray-600 dark:text-gray-400 truncate">
            {username}
          </span>
        </div>
        
        {/* Theme toggle */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500 dark:text-gray-400">Theme</span>
          <ThemeToggle />
        </div>
        
        {/* Sign out button */}
        <button
          onClick={() => {
            logout();
            closeSidebar();
          }}
          className="w-full flex justify-center items-center px-2 py-2 text-sm font-medium border border-red-500 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-200"
        >
          <ArrowLeftOnRectangleIcon className="h-4 w-4 mr-2" />
          Sign Out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 scroll-smooth-touch text-render-optimized">
      <div className="flex min-h-screen">
        {/* Desktop Sidebar */}
        <div className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0 md:z-50">
          <div className="flex flex-col flex-grow overflow-y-auto bg-white/90 dark:bg-gray-800/95 border-r border-gray-200 dark:border-gray-700 shadow-xl">
            <SidebarContent />
          </div>
        </div>

        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-50 md:hidden">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity duration-300"
              onClick={closeSidebar}
            />
            
            {/* Sidebar */}
            <div className="fixed inset-y-0 left-0 w-64 bg-white dark:bg-gray-800 shadow-xl transform transition-transform duration-300 ease-in-out">
              {/* Close button */}
              <div className="absolute top-4 right-4">
                <button
                  onClick={closeSidebar}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              <SidebarContent />
            </div>
          </div>
        )}

        {/* Main content */}
        <div className="flex flex-col flex-1 md:ml-64">
          {/* Mobile Header */}
          <div className="md:hidden sticky top-0 z-40">
            <div className="flex items-center justify-between p-4 bg-white/95 dark:bg-gray-800/95 border-b border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200"
                >
                  <Bars3Icon className="h-6 w-6" />
                </button>
                <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Bomberman Hunt
                </h1>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleEnterGame}
                  className="px-3 py-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white text-sm font-medium rounded-lg transition-all duration-200 shadow-md"
                >
                  Enter Game
                </button>
                <ThemeToggle />
              </div>
            </div>
          </div>

          {/* Main content area */}
          <main className="flex-1 relative overflow-y-auto focus:outline-none bg-white/30 dark:bg-gray-800/30 backdrop-blur-sm">
            <div className="py-4 md:py-8">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-10">
                {children}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;
